import { Component, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import {
    ITCIHybridDbConnection,
    TCITenantSelectorType, USER_ROLES,
} from "@tci/common";
import { Observable, Subscription } from "rxjs";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { ITCICEnvToolsComponentOutput, ITCIEnvToolsComponentInput } from "../../ng-tenant/tci-tenant-components";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { BaseHybridConnectionService } from "./base-hybrid-connection.sevice";

@Component({
    selector: TCITenantSelectorType.TCI_ENV_TOOLS_HYBRID_CONNECTION.toString(),
    templateUrl: "./base-hybrid-connection.component.html",
    styleUrls: ["./base-hybrid-connection.component.less"]
})
export class BaseHybridConnectionComponent
    extends AbstractTCITenantComponent <EventEmitter<string>, ITCIEnvToolsComponentInput, ITCICEnvToolsComponentOutput<any>>
    implements OnInit, OnDestroy {
    isToggledDBService = false;     // this value is for checking if user has changed the status by clicking the toggle button
    isDBServiceChecked = false;     // this value is the DB Service is enabled or not
    isTesterServiceChecked = false;
    private isADMRoleUser = false;
    private subscription: Subscription[] = [];
    private loopSubscription: Subscription;
    loading = true;
    loadingText;

    constructor(private dbConnService: BaseHybridConnectionService, private toastr: NgToastrService,
                private translate: TranslateService) {
        super();
        this.loadingText = this.translate.instant("NG_ENV_TOOLS.HYBRID_DB_CON_LOADING");
    }

    ngOnInit(): void {
        this.isToggledDBService = false;
        this.getUserAuthForDBService();
        this.pullingDBServiceStatus();
        this.getTesterServices();
    }

    getUserAuthForDBService() {
        const thisObj = this;
        this.dbConnService.getUserAuthForDBService()
            .subscribe(userAuth => {
                thisObj.isADMRoleUser = (userAuth.role === USER_ROLES.ADM);
                if (!thisObj.isADMRoleUser) {
                    this.toastr.info("Only Admin users can modify the Hybrid Connection");
                }
            });
    }

    /**
     * Get the status of the DBServices
     * calling this API 2 times with 5 seconds interval, and then take the last response
     */
    pullingDBServiceStatus() {
        this.loopSubscription = Observable.interval(5000)   // wait for 5 seconds for each API call
            .take(2) // Make 2 API calls
            .mergeMap(() => this.dbConnService.getDBServiceStatus()) // Merge each interval with an API call
            .toArray() // Collect all responses into an array
            .map(res => res.pop()) // Take the last response from the array
            .subscribe(
                (res: ITCIHybridDbConnection) => {
                    this.isDBServiceChecked = res.status.toLowerCase() === "enabled";
                    if (this.isToggledDBService) {
                        this.toastr.success(`DBService ${res.status} successfully`);
                    }
                },
                err => {
                    this.toastr.error(`DBService error: ${err.message}`);
                },
                () => {
                    this.loading = false;
                }
            );
    }

    stopDBServiceMonitor() {
        if (this.loopSubscription) {
            this.loading = false;
            this.loadingText = "";
            this.loopSubscription.unsubscribe();
        }
    }

    toggleDBStatus() {
        this.isToggledDBService = true;
        if (this.isDBServiceChecked) {
            this.disableDBService();
        } else {
            this.enableDBServiceWithSystemKey();
        }
    }

    enableDBServiceWithSystemKey() {
        this.loading = true;
        this.loadingText = this.translate.instant("NG_ENV_TOOLS.HYBRID_DB_CON_ENABLE");
        // Note: for IPAS-22509, UI should not call AO API /orchestrator/v1/hybrid/proxyapp
        // https://tibco.slack.com/archives/C057H5X7CJK/p1691438592780259?thread_ts=1691419050.789319&cid=C057H5X7CJK
        this.subscription.push(
            this.dbConnService.enableDBService()
                .subscribe(() => {
                    this.pullingDBServiceStatus();
                }, () => {
                    this.toastr.error(this.translate.instant("NG_ENV_TOOLS.HYBRID_DB_CON_ERROR"));
                    this.loading  = false;
                })
        );
    }

    disableDBService() {
        this.loading = true;
        this.loadingText = this.translate.instant("NG_ENV_TOOLS.HYBRID_DB_CON_DISABLE");
        this.subscription.push(
            this.dbConnService.disableDBService()
                .subscribe(() => {
                    this.pullingDBServiceStatus();
                }, () => {
                    this.toastr.error(this.translate.instant("NG_ENV_TOOLS.HYBRID_DB_CON_ERROR"));
                    this.loading = false;
                })
        );
    }

    toggleTesterStatus() {
        this.isTesterServiceChecked = !this.isTesterServiceChecked;
        if (!this.isTesterServiceChecked) {
            this.disableTesterServices();
        } else {
            this.enableTesterServices();
        }
    }

    getTesterServices() {
        this.dbConnService.getFlogoTesterServices()
            .subscribe((res) => {
                this.isTesterServiceChecked = res && (res.status === "enabled");
            }, (err) => {
                this.toastr.error("Error in getting flogo tester services status");
            });
    }

    enableTesterServices() {
        this.dbConnService.enableFlogoTesterServices()
            .subscribe((res) => {
                this.toastr.success(`Flogo tester services enabled successfully`);
            }, (err) => {
                this.handleError(err, "Error in enabling flogo tester services");
                this.isTesterServiceChecked = false;
            });
    }

    disableTesterServices() {
        this.dbConnService.disableFlogoTesterServices()
            .subscribe((res) => {
                this.toastr.success(`Flogo tester services disabled successfully`);
            }, (err) => {
                this.handleError(err, "Error in disabling flogo tester services");
                this.isTesterServiceChecked = true;
            });
    }

    handleError(err: any, defaultMsg: string) {
        let errorMsg;
        try {
            errorMsg = JSON.parse(err._body).errorMsg;
        } catch (e) {
            console.error("Unable to parse error body ", e);
        }
        this.toastr.error(errorMsg || defaultMsg);
    }

    ngOnDestroy(): void {
        this.subscription.forEach((res) => res.unsubscribe());
        this.stopDBServiceMonitor();
    }
}
